import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getMe } from '../../api/rbac';
import { RESET_STATE } from '../../store/resetActions';

const initialState = {
  permissions: [],
  roles: [],
  accountId: null, // Track which user these permissions belong to
  loading: false,
  loaded: false,
  error: null,
};

/**
 * Fetch current user's permissions and roles from /v2/me
 * @param {string} accountId - The accountId of the user requesting permissions
 */
export const fetchPermissions = createAsyncThunk(
  'permissions/fetchPermissions',
  async (accountId, { rejectWithValue }) => {
    try {
      const data = await getMe();
      // Include the accountId so we can verify ownership when storing
      return { ...data, accountId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch permissions');
    }
  }
);

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    clearPermissions: (state) => {
      state.permissions = [];
      state.roles = [];
      state.accountId = null;
      state.loaded = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPermissions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.accountId = action.payload?.accountId || null;
        state.permissions = action.payload?.permissions || [];
        state.roles = action.payload?.roles || [];
        state.error = null;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.error = action.payload || 'Failed to fetch permissions';
        // Fail open - empty permissions rather than blocking user
        state.permissions = [];
        state.roles = [];
        // Preserve the accountId from the request to prevent infinite retry loops
        // (if accountId were null, usePermissions would see mismatch and retry)
        state.accountId = action.meta.arg || null;
      })
      // Clear permissions on global state reset (logout)
      .addCase(RESET_STATE, () => initialState);
  },
});

export const { clearPermissions } = permissionsSlice.actions;

// Selectors
export const selectPermissions = (state) => state.permissions.permissions;
export const selectRoles = (state) => state.permissions.roles;
export const selectPermissionsAccountId = (state) => state.permissions.accountId;
export const selectPermissionsLoading = (state) => state.permissions.loading;
export const selectPermissionsLoaded = (state) => state.permissions.loaded;
export const selectPermissionsError = (state) => state.permissions.error;

export default permissionsSlice.reducer;
